import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import JourneyRail, { JourneyRailProps, StepBadge } from './JourneyRail';
import { Sheet } from '../ui';

/**
 * The wizard's journey on a phone: one line that says where you are, and the
 * whole rail behind it when you ask.
 *
 * The desktop rail has two shapes and neither survives the trip. The pinned
 * column is 170px of a 375px screen, taken from the step content, which is the
 * work. The slim rail is revealed by hovering, and a finger cannot hover. So
 * the journey becomes what it actually is on a small screen — orientation you
 * consult and then dismiss — and gives all of its width back until it is asked
 * for.
 *
 * The line still carries the two facts the rail carries at a glance: which
 * step this is, and how far through the five it sits. Opening it is the same
 * rail the desktop shows, with the same states, the same gating and the same
 * tip; nothing about the journey is a different thing here, only where it
 * lives while you are not reading it.
 */
export type WizardJourneyBarProps = Omit<JourneyRailProps, 'pinned' | 'onTogglePin' | 'className'>;

export const WizardJourneyBar: React.FC<WizardJourneyBarProps> = ({
  steps, locked, onStepSelect, onSubStepSelect, ...rest
}) => {
  const [open, setOpen] = useState(false);

  const activeIndex = steps.findIndex((step) => step.state === 'active');
  const active = activeIndex >= 0 ? steps[activeIndex] : steps[0];
  // Step 3's sub-step is the more useful label while you are inside it — the
  // rail's own active sub-item, not the parent "Supplier Discovery".
  const activeSub = active?.subSteps?.find((sub) => sub.state === 'active');

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        className="flex w-full shrink-0 items-center gap-s4 border-b border-line bg-surface px-s5 py-s3 text-left"
      >
        <StepBadge index={activeIndex + 1} state={active?.state ?? 'active'} />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-[13px] font-semibold text-ink">
            {activeSub?.label ?? active?.label ?? 'RFQ journey'}
          </span>
          <span className="text-meta text-ink-muted">
            Step {activeIndex + 1} of {steps.length}
          </span>
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-s2 text-meta font-semibold text-action">
          Journey
          <ChevronDown className="h-[14px] w-[14px]" />
        </span>
      </button>

      <Sheet open={open} onClose={() => setOpen(false)} side="left" label="RFQ journey">
        <JourneyRail
          {...rest}
          steps={steps}
          locked={locked}
          // Picking a step is the reason the drawer was opened; leaving it
          // standing over the step somebody just chose would make them
          // dismiss it to see what they asked for.
          onStepSelect={(id) => {
            setOpen(false);
            onStepSelect?.(id);
          }}
          onSubStepSelect={(id) => {
            setOpen(false);
            onSubStepSelect?.(id);
          }}
          className="h-full w-full overflow-y-auto"
        />
      </Sheet>
    </>
  );
};

export default WizardJourneyBar;
